import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Divider, ListSubheader, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Image from '../../Image';
// hooks
import useLocales from '../../../hooks/useLocales';

import { useDispatch, useSelector } from '../../../redux/store';
import { openLoginSignup } from '../../../redux/slices/authPages';

import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../Iconify';
import palette from '../../../theme/palette';
import LanguagePopover from '../../../layouts/dashboard/header/LanguagePopover';
import useGameNav from '../../../hooks/useGameNav';
import useAuth from '../../../hooks/useAuth';
import { IconFilter } from '../../../config';
import { openConfirm } from '../../../redux/slices/confirm';
import NotificationPopover from '../../../layouts/dashboard/header/NotificationPopover';
import { useLangNavigate } from '../../../hooks/useLangNavigate';

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  })
);

// ----------------------------------------------------------------------

NavSectionVertical.propTypes = {
  isCollapse: PropTypes.bool,
  navConfig: PropTypes.array,
  onCloseSidebar: PropTypes.func,
};

const NavWrapper = styled(Box)(({ theme, isDesktop }) => ({
  background: isDesktop ? '#0F3B22' : 'linear-gradient(90deg, #D7D7D7 0%, #BEBEBE 100%) !important',
  height: 45,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: 5,
  paddingLeft: '15px',
  position: 'relative',
  '&:hover': {
    background: 'linear-gradient(90deg, #0A5037 0%, #053318 100%)',
  },
}));

const NavImage = styled(Box)(({ theme }) => ({
  width: 30,
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const NavBtnText = styled(Typography)(({ theme, isDesktop }) => ({
  fontSize: isDesktop ? '1rem' : '1.2rem !important',
  fontWeight: 500,
  lineHeight: '1.5',
  // textAlign: 'center',
  textDecoration: 'none',
  color: isDesktop ? palette.navBarVertical.menu.color : '#383838',
  marginLeft: '16px',
}));

const CustomNav = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(180deg, #00431B 0%, #057734 100%)',
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  height: 60,
  cursor: 'pointer',
  position: 'relative',
  borderRadius: 5,
  padding: '10px 0 10px 16px',
}));

const SocialBox = styled(Image)(({ theme, isDesktop }) => ({
  width: isDesktop ? 28 : 40,
  height: isDesktop ? 28 : 40,
  margin: '0px 2px',
  cursor: 'pointer',
}));

const SocialText = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  margin: '20px 0',
  color: '#383838',
  fontWeight: 600,
  textAlign: 'center',
  marginTop: '40px !important',
}));

export default function NavSectionVertical({
  navConfig,
  isCollapse = false,
  onCloseSidebar = () => {},
  isDesktop,
  ...other
}) {
  const { logout } = useAuth();

  const dispatch = useDispatch();

  const navigate = useLangNavigate();

  const { translate } = useLocales();

  const { gameCategsAndCompanies, siteSettings, promotions } = useSelector((x) => ({
    ...x.game,
    ...x.lookup,
  }));

  const isAuthenticated = localStorage.getItem('accessToken');

  const [referralDepositBonusId, setReferralDepositBonusId] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const referralDepositBonusType = promotions?.data?.filter((f) => f.promotion_type === 'referral_deposit_bonus');

    if (referralDepositBonusType?.length > 0) {
      setReferralDepositBonusId(referralDepositBonusType[0]?.id);
    }
  }, [promotions]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const onOpenSocial = (link) => {
    window.open(link, '_blank');
  };

  const makeGameCategory = (_category) => {
    let nav = '';

    if (_category?.slug.toLowerCase().indexOf('slots') !== -1)
      nav = { src: require('../../../assets/menu/nav-slot.png') };

    if (_category?.slug.toLowerCase().indexOf('live-casino') !== -1)
      nav = { src: require('../../../assets/menu/nav-live.png') };

    if (_category?.slug.toLowerCase().indexOf('fishing') !== -1)
      nav = { src: require('../../../assets/menu/nav-fish.png') };

    if (_category?.slug.toLowerCase().indexOf('card') !== -1)
      nav = { src: require('../../../assets/menu/nav-card.png') };

    if (_category?.slug.toLowerCase().indexOf('sports') !== -1)
      nav = { src: require('../../../assets/menu/nav-sport.png') };

    if (_category?.slug.toLowerCase().indexOf('crash-game') !== -1)
      nav = { src: require('../../../assets/menu/nav-crash.png') };

    if (_category?.slug.toLowerCase().indexOf('e-sports') !== -1) nav = { icon: 'streamline:esports-solid' };

    if (_category?.slug.toLowerCase().indexOf('instant-game') !== -1) nav = { icon: 'ion:rocket-sharp' };

    if (_category?.slug.toLowerCase().indexOf('exchange') !== -1)
      nav = { src: require('../../../assets/menu/nav-exchange.png') };

    return (
      <NavWrapper
        key={_category?.key}
        onClick={() => {
          if (!isDesktop) onCloseSidebar();
          navigate(`/games/${_category?.slug}`);
        }}
        isDesktop={isDesktop}
        // sx={activeTab === _category?.slug ? { backgroundColor: 'background.paper' } : {}}
      >
        <NavImage>
          {nav?.src ? (
            <Image
              src={nav.src}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                filter: IconFilter,
                height: '30px',
                width: '30px',
              }}
            />
          ) : (
            <></>
          )}
          {nav?.icon ? (
            <Iconify icon={nav.icon} sx={{ width: '30px', height: '30px', color: palette.home.iconColor }} />
          ) : (
            <></>
          )}
        </NavImage>
        <NavBtnText isDesktop={isDesktop}>{_category?.name}</NavBtnText>
      </NavWrapper>
    );
  };

  return (
    <Box {...other}>
      {!isDesktop && (
        <Stack direction={'row'} sx={{ alignItems: 'center', background: '#094221', p: '20px 20px', mb: '20px' }}>
          <Image src={require('../../../assets/brand-logo.png')} sx={{ maxWidth: '140px', height: 'auto', flex: 1 }} />
        </Stack>
      )}

      <Stack sx={{ pb: 1, px: { md: 0, sm: '40px', xs: '40px' } }} spacing={isDesktop ? '5px' : '10px'}>
        {/* {angPauPromos?.data?.length ? (
          <Box onClick={() => navigate('/angpau')}>
            <Image src={require('../../../assets/menu/nav-earn.png')} />
          </Box>
        ) : (
          ''
        )} */}

        {/* <CustomNav>
          <Stack direction="row" spacing={2}>
            <Image src={require('../../../assets/home/bonus.png')} sx={{ width: '45px' }} />
            <Typography>{translate('promotion')}</Typography>
          </Stack>
        </CustomNav> */}

        <CustomNav
          sx={{ background: 'linear-gradient(180deg,#432800,#774505)' }}
          onClick={() => {
            if (isAuthenticated) navigate('/spin');
            else dispatch(openLoginSignup({ open: true, isLogin: true }));
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Image src={require('../../../assets/home/spin.png')} sx={{ width: '45px' }} />
            <Typography>{translate('spin_the_wheel')}</Typography>
          </Stack>
        </CustomNav>

        <CustomNav
          onClick={() => navigate('/download')}
          sx={{ background: 'linear-gradient(180deg, #e5b92a 0%, #c8910e 100%)' }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Image src={require('../../../assets/home/android.png')} sx={{ width: '45px' }} />
            <Typography>{translate('download')}</Typography>
          </Stack>
        </CustomNav>

        <NavWrapper isDesktop={isDesktop} onClick={() => navigate('/home')}>
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-home.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                height: '25px',
                width: '25px',
              }}
            />
          </NavImage>
          <NavBtnText isDesktop={isDesktop}>{translate('home')}</NavBtnText>
        </NavWrapper>

        {isAuthenticated && !isDesktop ? (
          <NotificationPopover
            CustomBtn={
              <NavWrapper sx={{ width: '100%' }}>
                <NavImage>
                  <Iconify
                    icon="mdi:bell"
                    sx={{
                      mx: 'auto',
                      cursor: 'pointer',
                      height: '33px',
                      width: '33px',
                      color: palette.home.iconColor,
                    }}
                  />
                </NavImage>
                <NavBtnText>{translate('notifications')}</NavBtnText>
              </NavWrapper>
            }
          />
        ) : (
          ''
        )}

        {/* <NavWrapper
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('/vip');
          }}
        >
          <NavImage>
            <Iconify icon={'quill:vip'} sx={{ color: palette.home.iconColor, width: '100%', height: '100%' }} />
          </NavImage>
          <NavBtnText>{translate('VIP Club')}</NavBtnText>
        </NavWrapper> */}

        {gameCategsAndCompanies.length > 0 &&
          gameCategsAndCompanies.map((item, index) => <Box key={index}>{makeGameCategory(item)}</Box>)}

        <NavWrapper isDesktop={isDesktop} onClick={() => navigate('/promotion')}>
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-promo.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                height: '25px',
                width: '25px',
              }}
            />
          </NavImage>
          <NavBtnText isDesktop={isDesktop}>{translate('promotions')}</NavBtnText>
        </NavWrapper>

        {/* {isAuthenticated ? (
          <NavWrapper onClick={() => navigate('/promotion/voucher')}>
            <NavImage>
              <Iconify
                icon={'mdi:ticket'}
                sx={{ color: palette.home.iconColor, width: '25px', height: '25px', display: 'block', mx: 'auto' }}
              />
            </NavImage>
            <NavBtnText>{translate('apply_voucher')}</NavBtnText>
          </NavWrapper>
        ) : (
          ''
        )} */}

        {/* <NavWrapper onClick={() => navigate('/betting_pass')}>
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-betting-pass.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                height: '25px',
                width: '25px',
              }}
            />
          </NavImage>
          <NavBtnText>{translate('betting_pass')}</NavBtnText>
        </NavWrapper> */}

        {/* <NavWrapper
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('user/wallet');
            onCloseSidebar();
          }}
        >
          <NavImage sx={{ justifyContent: 'center' }}>
            <Iconify
              icon={'mdi:wallet-plus'}
              sx={{ color: palette.home.iconColor, width: '25px', height: '25px', display: 'block', mx: 'auto' }}
            />
          </NavImage>
          <NavBtnText>{translate('deposit')}</NavBtnText>
        </NavWrapper> */}

        <NavWrapper
          isDesktop={isDesktop}
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('/vip');
            // if (referralDepositBonusId) navigate(`/promotion/view/${referralDepositBonusId}`);
            onCloseSidebar();
          }}
        >
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-vip.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                height: '33px',
                width: '33px',
              }}
            />
          </NavImage>
          <NavBtnText isDesktop={isDesktop}>{translate('vip')}</NavBtnText>
        </NavWrapper>

        <NavWrapper
          isDesktop={isDesktop}
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('/referral');
            // if (referralDepositBonusId) navigate(`/promotion/view/${referralDepositBonusId}`);
            onCloseSidebar();
          }}
        >
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-referral.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                height: '33px',
                width: '33px',
              }}
            />
          </NavImage>
          <NavBtnText isDesktop={isDesktop}>{translate('referral')}</NavBtnText>
        </NavWrapper>
        <NavWrapper
          isDesktop={isDesktop}
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('/history/bet');
            if (!isDesktop) onCloseSidebar();
          }}
        >
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-bet-history.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
              }}
            />
          </NavImage>
          <NavBtnText isDesktop={isDesktop}>{translate('game_history')}</NavBtnText>
        </NavWrapper>

        {isDesktop && <Divider sx={{ borderColor: '#115B30', pt: '5px' }} />}

        {!isDesktop && <SocialText>{translate('social_media')}</SocialText>}

        <Stack
          direction={'row'}
          spacing={isDesktop ? '5px' : '20px'}
          sx={{ justifyContent: 'center', alignItems: 'center', pt: '5px', pb: isDesktop ? '0' : '5rem' }}
        >
          {siteSettings?.config_instagram && (
            <Box onClick={() => onOpenSocial(siteSettings?.config_instagram)} sx={{ cursor: 'pointer' }}>
              <SocialBox isDesktop={isDesktop} src={require('../../../assets/social/instagram.png')} />
            </Box>
          )}
          {siteSettings?.config_facebook && (
            <Box onClick={() => onOpenSocial(siteSettings?.config_facebook)} sx={{ cursor: 'pointer' }}>
              <SocialBox isDesktop={isDesktop} src={require('../../../assets/social/fb.png')} />
            </Box>
          )}
          {siteSettings?.config_telegram && (
            <Box onClick={() => onOpenSocial(siteSettings?.config_telegram)} sx={{ cursor: 'pointer' }}>
              <SocialBox isDesktop={isDesktop} src={require('../../../assets/social/telegram.png')} />
            </Box>
          )}
          {siteSettings?.config_whatsapp && (
            <Box onClick={() => onOpenSocial(siteSettings?.config_whatsapp)} sx={{ cursor: 'pointer' }}>
              <SocialBox isDesktop={isDesktop} src={require('../../../assets/social/wa.png')} />
            </Box>
          )}
        </Stack>

        {isDesktop && <Divider sx={{ borderColor: '#115B30', pt: '5px' }} />}

        {isAuthenticated ? (
          <>
            {/* <NavWrapper
              isDesktop={isDesktop}
              onClick={() => {
                navigate('/history/bet');
                if (!isDesktop) onCloseSidebar();
              }}
            >
              <NavImage>
                <Image
                  src={require('../../../assets/menu/nav-bet-history.png')}
                  sx={{
                    mx: 'auto',
                    cursor: 'pointer',
                  }}
                />
              </NavImage>
              <NavBtnText>{translate('game_history')}</NavBtnText>
            </NavWrapper> */}
            {/* <NavWrapper
              onClick={() => {
                navigate('/history/transaction');
                if (!isDesktop) onCloseSidebar();
              }}
            >
              <NavImage>
                <Iconify
                  icon={'grommet-icons:transaction'}
                  sx={{ color: palette.home.iconColor, width: '25px', height: '25px' }}
                />
              </NavImage>
              <NavBtnText>{translate('transaction_history')}</NavBtnText>
            </NavWrapper> */}
            {/* <NavWrapper
              isDesktop={isDesktop}
              onClick={() =>
                openConfirm({
                  title: translate('logout_confirmation'),
                  subTitle: translate('are_you_sure_to_logout'),
                  onConfirm: handleLogout,
                })
              }
            >
              <NavImage>
                <Iconify
                  icon={'clarity:logout-line'}
                  sx={{ color: palette.home.iconColor, width: '25px', height: '25px' }}
                />
              </NavImage>
              <NavBtnText>{translate('logout')}</NavBtnText>
            </NavWrapper> */}
          </>
        ) : (
          <></>
        )}

        {/* <LanguagePopover
          CustomBtn={
            <NavWrapper>
              <NavImage>
                <Image
                  src={require('../../../assets/menu/nav-lang.png')}
                  sx={{
                    mx: 'auto',
                    cursor: 'pointer',
                    height: '30px',
                    width: '30px',
                  }}
                />
              </NavImage>
              <NavBtnText>{translate('language')}</NavBtnText>
            </NavWrapper>
          }
        /> */}

        {/* <NavWrapper
          onClick={() => {
            if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
            else navigate('/referral');
            // if (referralDepositBonusId) navigate(`/promotion/view/${referralDepositBonusId}`);
            onCloseSidebar();
          }}
        >
          <NavImage>
            <Image
              src={require('../../../assets/menu/nav-lang.png')}
              sx={{
                mx: 'auto',
                cursor: 'pointer',
                filter:
                  IconFilter,
              }}
            />
          </NavImage>
          <NavBtnText>{translate('language')}</NavBtnText>
        </NavWrapper> */}
        {/* <NavBottomBtn
            title={translate('Contact Us')}
            icon={'fluent:person-support-24-regular'}
          />
          <NavBottomBtn title={translate('FAQ')} icon={'octicon:question-24'} />
          <NavBottomBtn title={translate('Download')} icon="solar:download-linear" /> */}
        {/* <LanguagePopover
            CustomBtn={<NavBottomBtn icon="octicon:globe-24" />}
            PopoverProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              disabledArrow: true,
            }}
          /> */}
      </Stack>
    </Box>
  );
}
